/**
 * Staff Inbox API functions
 */

import { apiFetch, withQuery } from '@/evennia_replacements/api';
import type { PaginatedResponse } from '@/shared/types';
import type {
  AccountHistory,
  AccountInvite,
  AccountInviteStatus,
  BugReport,
  GMApplication,
  GMApplicationStatus,
  FiledIssue,
  InboxResponse,
  PlayerFeedback,
  PlayerReport,
  RosterApplicationDetail,
  RosterApplicationListItem,
  RosterApplicationReviewResult,
  RosterApplicationStatus,
  SubmissionCategory,
  SubmissionStatus,
  SystemErrorReport,
} from './types';

const INBOX_URL = '/api/staff-inbox';
const SUBMISSIONS_URL = '/api/player-submissions';
const GM_URL = '/api/gm/applications';
const INVITES_URL = '/api/staff/invites';
const ROSTER_APPS_URL = '/api/roster/applications';

// =============================================================================
// Staff Inbox
// =============================================================================

export async function getStaffInbox(
  categories?: SubmissionCategory[],
  page?: number,
  pageSize?: number,
  includeIgnored?: boolean
): Promise<InboxResponse> {
  const params = new URLSearchParams();
  if (categories) {
    for (const cat of categories) {
      params.append('categories', cat);
    }
  }
  if (includeIgnored) {
    params.append('include_ignored', 'true');
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  if (pageSize != null) {
    params.append('page_size', pageSize.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${INBOX_URL}/`, qs));
  if (!res.ok) throw new Error('Failed to load staff inbox');
  return res.json();
}

export async function getAccountHistory(accountId: number): Promise<AccountHistory> {
  const res = await apiFetch(`${INBOX_URL}/accounts/${accountId}/history/`);
  if (!res.ok) throw new Error('Failed to load account history');
  return res.json();
}

export async function getOpenSubmissionCount(): Promise<number> {
  // Exclude character_application to avoid double-counting with the separate applications badge
  const params = new URLSearchParams();
  params.append('categories', 'player_feedback');
  params.append('categories', 'bug_report');
  params.append('categories', 'player_report');
  params.append('page_size', '1');
  const res = await apiFetch(`${INBOX_URL}/?${params}`);
  if (!res.ok) return 0;
  const data: InboxResponse = await res.json();
  return data.count;
}

// =============================================================================
// Player Feedback
// =============================================================================

export async function getFeedbackList(
  status?: SubmissionStatus,
  page?: number
): Promise<PaginatedResponse<PlayerFeedback>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${SUBMISSIONS_URL}/feedback/`, qs));
  if (!res.ok) throw new Error('Failed to load feedback list');
  return res.json();
}

export async function getFeedbackDetail(id: number): Promise<PlayerFeedback> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/feedback/${id}/`);
  if (!res.ok) throw new Error('Failed to load feedback detail');
  return res.json();
}

export async function updateFeedbackStatus(
  id: number,
  status: SubmissionStatus
): Promise<PlayerFeedback> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/feedback/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update feedback status');
  return res.json();
}

// =============================================================================
// Bug Reports
// =============================================================================

export async function getBugReportList(
  status?: SubmissionStatus,
  page?: number
): Promise<PaginatedResponse<BugReport>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${SUBMISSIONS_URL}/bug-reports/`, qs));
  if (!res.ok) throw new Error('Failed to load bug report list');
  return res.json();
}

export async function getBugReportDetail(id: number): Promise<BugReport> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/bug-reports/${id}/`);
  if (!res.ok) throw new Error('Failed to load bug report detail');
  return res.json();
}

export async function updateBugReportStatus(
  id: number,
  status: SubmissionStatus
): Promise<BugReport> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/bug-reports/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update bug report status');
  return res.json();
}

// Staff-only: file a public GitHub issue from a report (#1164). title/body are the
// staff-edited, already-redacted text.
async function fileIssue(path: string, title: string, body: string): Promise<FiledIssue> {
  const res = await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify({ title, body }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail ?? 'Failed to file GitHub issue');
  }
  return res.json();
}

export function fileBugReportIssue(id: number, title: string, body: string): Promise<FiledIssue> {
  return fileIssue(`${SUBMISSIONS_URL}/bug-reports/${id}/file-issue/`, title, body);
}

export function fileSystemErrorIssue(id: number, title: string, body: string): Promise<FiledIssue> {
  return fileIssue(`${SUBMISSIONS_URL}/system-errors/${id}/file-issue/`, title, body);
}

// =============================================================================
// Player Reports
// =============================================================================

export async function getPlayerReportList(
  status?: SubmissionStatus,
  page?: number
): Promise<PaginatedResponse<PlayerReport>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${SUBMISSIONS_URL}/player-reports/`, qs));
  if (!res.ok) throw new Error('Failed to load player report list');
  return res.json();
}

export async function getPlayerReportDetail(id: number): Promise<PlayerReport> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/player-reports/${id}/`);
  if (!res.ok) throw new Error('Failed to load player report detail');
  return res.json();
}

export async function updatePlayerReportStatus(
  id: number,
  status: SubmissionStatus
): Promise<PlayerReport> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/player-reports/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update player report status');
  return res.json();
}

// =============================================================================
// System Error Reports (auto-captured, #1164)
// =============================================================================

export async function getSystemErrorList(
  status?: SubmissionStatus,
  page?: number
): Promise<PaginatedResponse<SystemErrorReport>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${SUBMISSIONS_URL}/system-errors/`, qs));
  if (!res.ok) throw new Error('Failed to load system error list');
  return res.json();
}

export async function getSystemErrorDetail(id: number): Promise<SystemErrorReport> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/system-errors/${id}/`);
  if (!res.ok) throw new Error('Failed to load system error detail');
  return res.json();
}

export async function updateSystemErrorStatus(
  id: number,
  status: SubmissionStatus
): Promise<SystemErrorReport> {
  const res = await apiFetch(`${SUBMISSIONS_URL}/system-errors/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update system error status');
  return res.json();
}

// =============================================================================
// GM Applications
// =============================================================================

export async function getGMApplicationList(
  status?: GMApplicationStatus,
  page?: number
): Promise<PaginatedResponse<GMApplication>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${GM_URL}/`, qs));
  if (!res.ok) throw new Error('Failed to load GM application list');
  return res.json();
}

export async function getGMApplicationDetail(id: number): Promise<GMApplication> {
  const res = await apiFetch(`${GM_URL}/${id}/`);
  if (!res.ok) throw new Error('Failed to load GM application detail');
  return res.json();
}

export async function updateGMApplication(
  id: number,
  data: { status?: GMApplicationStatus; staff_response?: string }
): Promise<GMApplication> {
  const res = await apiFetch(`${GM_URL}/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update GM application');
  return res.json();
}

// =============================================================================
// Account Invites (#3054)
// =============================================================================

export async function getAccountInviteList(
  status?: AccountInviteStatus,
  page?: number
): Promise<PaginatedResponse<AccountInvite>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const res = await apiFetch(withQuery(`${INVITES_URL}/`, qs));
  if (!res.ok) throw new Error('Failed to load invite list');
  return res.json();
}

export async function issueAccountInvite(email: string, note?: string): Promise<AccountInvite> {
  const res = await apiFetch(`${INVITES_URL}/`, {
    method: 'POST',
    body: JSON.stringify({ email, note: note ?? '' }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.email?.[0] ?? data?.detail ?? 'Failed to issue invite');
  }
  return res.json();
}

export async function revokeAccountInvite(id: number): Promise<AccountInvite> {
  const res = await apiFetch(`${INVITES_URL}/${id}/revoke/`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to revoke invite');
  return res.json();
}

export async function resendAccountInvite(id: number): Promise<AccountInvite> {
  const res = await apiFetch(`${INVITES_URL}/${id}/resend/`, { method: 'POST' });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail ?? 'Failed to resend invite');
  }
  return res.json();
}

// =============================================================================
// Roster-Character Applications (#3265)
// =============================================================================

export async function getRosterApplications(
  status?: RosterApplicationStatus,
  page?: number
): Promise<PaginatedResponse<RosterApplicationListItem>> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (page != null) {
    params.append('page', page.toString());
  }
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : '';
  const res = await apiFetch(`${ROSTER_APPS_URL}/${suffix}`);
  if (!res.ok) throw new Error('Failed to load roster application list');
  return res.json();
}

export async function getRosterApplicationDetail(id: number): Promise<RosterApplicationDetail> {
  const res = await apiFetch(`${ROSTER_APPS_URL}/${id}/`);
  if (!res.ok) throw new Error('Failed to load roster application detail');
  return res.json();
}

export async function reviewRosterApplication(
  id: number,
  action: 'approve' | 'deny',
  reviewNotes?: string
): Promise<RosterApplicationReviewResult> {
  const res = await apiFetch(`${ROSTER_APPS_URL}/${id}/review/`, {
    method: 'POST',
    body: JSON.stringify({ action, review_notes: reviewNotes ?? '' }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail ?? 'Failed to review roster application');
  }
  return res.json();
}

export async function getPendingRosterApplicationCount(): Promise<number> {
  const res = await apiFetch(`${ROSTER_APPS_URL}/pending-count/`);
  if (!res.ok) return 0;
  const data = await res.json();
  return data.count;
}

// Staff-only fallback when mail cannot reach the player (#3193): produces the
// email-verification link for an unverified account so staff can hand it over
// directly, mirroring the invite Copy Link flow.
export async function fetchVerificationLink(
  email: string
): Promise<{ email: string; link: string }> {
  const res = await apiFetch('/api/staff/verification-link/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail ?? 'Failed to generate verification link');
  }
  return res.json();
}
